import type { IconName } from "@similarweb/icons";

import { chosenSites } from "app/common/services/chosenSites";
import { useApiQuery } from "app/modules/api-client";
import type { Data } from "app/modules/api-client/queryTypes";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { getFaviconUrl } from "app/services/sitesService/sitesUtils";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";

type CreativeType = "banner" | "native" | "text" | "video";

interface ICreativeTypeInfo {
    icon: IconName;
    name: string;
    tooltip: string;
    isNewDataFormat?: boolean;
}

export const creativeTypeToInfoMap: Record<CreativeType, ICreativeTypeInfo> = {
    banner: {
        icon: "banner",
        name: "ad.intelligence.advertiser.ad.type.banner",
        tooltip: "ad.intelligence.advertiser.ad.type.banner.tooltip",
    },
    native: {
        icon: "native-ad",
        name: "ad.intelligence.advertiser.ad.type.native",
        tooltip: "ad.intelligence.advertiser.ad.type.native.tooltip",
        isNewDataFormat: true,
    },
    text: {
        icon: "text",
        name: "ad.intelligence.advertiser.ad.type.text",
        tooltip: "ad.intelligence.advertiser.ad.type.text.tooltip",
        isNewDataFormat: true,
    },
    video: {
        icon: "play",
        name: "ad.intelligence.advertiser.ad.type.video",
        tooltip: "ad.intelligence.advertiser.ad.type.video.tooltip",
    },
};

type AdsGalleryApiDataItem = NonNullable<
    | NonNullable<Data<"AdIntelligenceMarketingAssets", "adIntelligenceAdvertiserAdsGalleryUnifiedCreate">>["data"]
    | NonNullable<Data<"AdIntelligenceMarketingAssets", "adIntelligencePublisherAdsGalleryUnifiedCreate">>["data"]
>[number];

export type AdsGalleryData = AdsGalleryApiDataItem & {
    landingPagesLinks?: string[];
    advertiserFavicon?: string;
};

export const useAdsGallery = (
    controller: AdIntelligenceController,
    page: number,
    pageSize: number,
    sort?: string,
    campaigns?: string[],
    creativeType?: string,
    publishers?: string[],
) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const selectedSite = params.adsSelectedSite ?? chosenSites.getPrimarySite()?.name;
    const { from, to, isWindow } = DurationService.getDurationData(
        params.duration ?? BasicDurations.LAST_TWENTY_EIGHT_DAYS,
    ).forAPI;

    const queryParams = {
        country: params.country,
        key: selectedSite ? [selectedSite] : [],
        from,
        to,
        isWindow,
        ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
    };

    const bodyParams = {
        pageSize: pageSize,
        page: page,
        sort: sort,
        asc: false,
        adsWindowMatchMode: 0 as const,
        ...(controller === "advertiser" && campaigns ? { campaignPages: campaigns } : {}),
        ...(sort ? { sort } : {}),
        ...(creativeType ? { creativeType: creativeType } : {}),
        ...(publishers && publishers.length > 0
            ? controller === "advertiser"
                ? { publishers: publishers }
                : { advertisers: publishers }
            : {}),
    };

    const methodName =
        controller === "advertiser"
            ? "adIntelligenceAdvertiserAdsGalleryUnifiedCreate"
            : "adIntelligencePublisherAdsGalleryUnifiedCreate";

    const {
        data: apiResponse,
        isLoading,
        isFetching,
        isError,
    } = useApiQuery(
        {
            tag: "AdIntelligenceMarketingAssets",
            methodName,
            options: {
                // Always return the same shape so `data` is AdsGalleryData[] on every
                // branch (a raw early-return would make the result a union).
                select: (adsData) => ({
                    ...adsData,
                    data: (adsData?.data ?? []).map(
                        (ad: AdsGalleryApiDataItem): AdsGalleryData => ({
                            ...ad,
                            landingPagesLinks: ad.landingPage ? [ad.landingPage] : undefined,
                            ...("advertiser" in ad && ad.advertiser
                                ? { advertiserFavicon: getFaviconUrl(ad.advertiser) }
                                : {}),
                        }),
                    ),
                }),
            },
        },
        queryParams,
        bodyParams,
    );

    return {
        data: isError ? undefined : apiResponse?.data,
        startDate: apiResponse?.startDate,
        totalCount: apiResponse?.totalCount,
        totalValidCount: apiResponse?.totalValidCount,
        isFromNewScraper: apiResponse?.isFromNewScraper,
        isLoading: isLoading || isFetching,
        isError,
    };
};
